import React from 'react';
import { Link } from 'react-router-dom';
import Disclaimer from '../../components/Disclaimer';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import { ArrowLeft, TrendingUp, Calculator, Lightbulb } from 'lucide-react';

const ComprendreCotesParis = () => {
  return (
    <div className="min-h-screen bg-black">
      <Disclaimer />
      <Header />
      <div className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <Link
            to="/guides"
            data-discover="true"
            className="inline-flex items-center gap-2 text-toxic-green hover:text-toxic-cyan mb-8 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Retour aux guides
          </Link>

          <div className="mb-8">
            <div className="inline-flex items-center gap-2 mb-4 px-4 py-2 glass rounded-full border border-toxic-green/30">
              <TrendingUp className="w-5 h-5 text-toxic-green" />
              <span className="text-sm text-toxic-green font-semibold">Guide pratique</span>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold mb-4 text-white">
              Comment comprendre les cotes
            </h1>
            <p className="text-xl text-white/70">
              Maîtrisez l'art de lire et d'interpréter les cotes pour maximiser vos gains
            </p>
          </div>

          <article className="glass-strong rounded-2xl p-8 md:p-12 border border-white/10 space-y-8 text-white/80 leading-relaxed">
            <section>
              <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">Qu'est-ce qu'une cote ?</h2>
              <p className="mb-4">
                Une cote est un nombre qui représente la probabilité qu'un événement se produise, selon l'opérateur de paris. Elle 
                détermine également le montant que vous gagnerez si votre pari est gagnant. Comprendre les cotes est fondamental pour 
                réussir dans les paris sportifs, car elles vous permettent d'évaluer la valeur d'un pari et de prendre des décisions 
                éclairées.
              </p>
              <p className="mb-4">
                Les cotes reflètent non seulement la probabilité réelle d'un événement, mais aussi la marge de l'opérateur et l'opinion 
                du marché. Les opérateurs ajustent constamment les cotes en fonction des paris reçus, des informations disponibles, 
                et de leur propre analyse. C'est pourquoi les cotes peuvent varier entre différents opérateurs et évoluer dans le temps.
              </p>
              <p>
                En France, les cotes sont généralement affichées au format décimal (par exemple, 2.50). Ce format indique le montant 
                total que vous recevrez pour chaque euro misé, gains inclus. Plus la cote est élevée, plus le gain potentiel est 
                important, mais moins l'événement est probable selon l'opérateur.
              </p>
            </section>

            <section>
              <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">Format décimal : le standard français</h2>
              <p className="mb-4">
                Le format décimal est le plus utilisé en France et dans la plupart des pays européens. Il est simple à comprendre : 
                si vous misez 10€ sur une cote de 2.50 et que votre pari gagne, vous recevrez 25€ au total (10€ x 2.50). Votre gain 
                net sera donc de 15€ (25€ - 10€ de mise initiale).
              </p>
              <div className="glass rounded-lg p-6 mb-4 border border-toxic-green/30">
                <div className="flex items-center gap-3 mb-3">
                  <Calculator className="w-6 h-6 text-toxic-green" />
                  <h3 className="font-semibold text-white">Exemple de calcul</h3>
                </div>
                <p className="mb-2">Mise : 20€</p>
                <p className="mb-2">Cote : 3.00</p>
                <p className="mb-2">Gain total : 20€ × 3.00 = 60€</p>
                <p className="text-toxic-green font-semibold">Gain net : 60€ - 20€ = 40€</p>
              </div>
              <p className="mb-4">
                Pour calculer votre gain potentiel avec une cote décimale, multipliez simplement votre mise par la cote. Le résultat 
                est le montant total que vous recevrez, y compris votre mise initiale. Pour obtenir votre gain net, soustrayez votre 
                mise initiale du montant total.
              </p>
              <p>
                Les cotes décimales facilitent également la comparaison entre différents opérateurs. Si un opérateur propose une cote 
                de 2.10 pour un match et qu'un autre propose 2.15, la différence peut sembler minime, mais sur de nombreux paris, 
                cette différence peut considérablement impacter vos gains à long terme.
              </p>
            </section>

            <section>
              <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">Interpréter les probabilités implicites</h2>
              <p className="mb-4">
                Chaque cote correspond à une probabilité implicite. Pour convertir une cote décimale en probabilité, divisez 1 par la 
                cote et multipliez par 100. Par exemple, une cote de 2.00 correspond à une probabilité implicite de 50% (1 ÷ 2.00 × 100).
              </p>
              <p className="mb-4">
                Cependant, cette probabilité implicite n'est pas la probabilité réelle de l'événement. Elle inclut la marge de l'opérateur, 
                qui est leur profit. Si vous additionnez toutes les probabilités implicites d'un événement (par exemple, victoire équipe 
                A, match nul, victoire équipe B), vous obtiendrez un total supérieur à 100%. Cette différence est la marge de l'opérateur.
              </p>
              <p>
                Les opérateurs avec des marges plus faibles offrent généralement de meilleures cotes à long terme. C'est pourquoi il est 
                important de comparer les cotes entre différents opérateurs avant de placer un pari. Même une différence de quelques 
                centièmes peut faire une différence significative sur de nombreux paris.
              </p>
            </section>

            <section>
              <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">Identifier la valeur d'un pari</h2>
              <p className="mb-4">
                Un pari a de la valeur lorsque la probabilité réelle d'un événement est supérieure à la probabilité implicite de la cote. 
                Par exemple, si vous estimez qu'une équipe a 60% de chances de gagner, mais que la cote correspond à une probabilité 
                implicite de 50%, ce pari a de la valeur.
              </p>
              <p className="mb-4">
                Identifier la valeur nécessite une analyse approfondie. Vous devez évaluer les forces et faiblesses des équipes, les 
                statistiques historiques, les blessures, la motivation, et de nombreux autres facteurs. Cette analyse vous permet de 
                former votre propre opinion sur la probabilité réelle d'un événement, indépendamment de ce que suggèrent les cotes.
              </p>
              <p className="mb-4">
                Les parieurs expérimentés ne parient pas sur tous les événements. Ils attendent des opportunités où ils identifient une 
                valeur significative. Cette approche sélective augmente leurs chances de succès à long terme, même si elle signifie 
                parier moins fréquemment.
              </p>
              <p>
                N'oubliez pas que même les paris avec de la valeur peuvent perdre. La variance fait partie intégrante des paris sportifs. 
                Cependant, sur un grand nombre de paris, les paris avec de la valeur génèrent généralement des profits à long terme, 
                même si certains paris individuels perdent.
              </p>
            </section>

            <section>
              <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">Types de cotes et marchés</h2>
              <p className="mb-4">
                Les cotes varient selon le type de marché. Les marchés simples, comme le résultat d'un match (victoire, match nul, défaite), 
                ont généralement des cotes plus basses car ils sont plus faciles à prédire. Les marchés plus spécifiques, comme le score 
                exact ou le nombre de buts d'un joueur précis, ont généralement des cotes plus élevées car ils sont plus difficiles à 
                prédire.
              </p>
              <p className="mb-4">
                Les cotes peuvent également varier selon le moment où vous pariez. Les cotes avant le match peuvent être différentes des 
                cotes en direct pendant le match. Les événements qui se produisent pendant le match (buts, cartons, blessures) modifient 
                les probabilités et donc les cotes. C'est pourquoi certains parieurs préfèrent parier en direct, où ils peuvent observer 
                le déroulement du match avant de prendre une décision.
              </p>
              <p>
                Les paris combinés (accumulateurs) multiplient les cotes de chaque sélection. Par exemple, si vous combinez trois paris 
                avec des cotes de 2.00, 3.00, et 4.00, votre cote combinée sera de 24.00 (2.00 × 3.00 × 4.00). Cependant, tous les paris 
                doivent gagner pour que votre pari combiné soit gagnant. C'est pourquoi les paris combinés offrent des gains potentiels 
                élevés mais avec un risque considérablement accru.
              </p>
            </section>

            <section>
              <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">Stratégies pour utiliser les cotes</h2>
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <Lightbulb className="w-6 h-6 text-toxic-green flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-semibold text-white mb-2">Comparez toujours les cotes</h3>
                    <p>
                      Avant de placer un pari, vérifiez les cotes proposées par plusieurs opérateurs. Les différences peuvent être 
                      significatives, surtout sur les événements moins populaires. Utilisez des comparateurs de cotes pour identifier 
                      rapidement les meilleures offres.
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Lightbulb className="w-6 h-6 text-toxic-green flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-semibold text-white mb-2">Ne vous fiez pas uniquement aux cotes</h3>
                    <p>
                      Les cotes reflètent l'opinion de l'opérateur et du marché, pas nécessairement la réalité. Faites votre propre 
                      analyse et formez votre propre opinion. Si vous identifiez une valeur, n'hésitez pas à parier, même si les cotes 
                      semblent défavorables.
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Lightbulb className="w-6 h-6 text-toxic-green flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-semibold text-white mb-2">Gérez votre bankroll selon les cotes</h3>
                    <p>
                      Adaptez la taille de vos mises selon la valeur identifiée et la cote. Les paris avec une valeur élevée et des cotes 
                      favorables peuvent justifier des mises plus importantes, tandis que les paris à long terme avec des cotes élevées 
                      devraient généralement être des mises plus petites.
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Lightbulb className="w-6 h-6 text-toxic-green flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-semibold text-white mb-2">Surveillez les mouvements de cotes</h3>
                    <p>
                      Les cotes évoluent constamment. Si vous observez un mouvement significatif des cotes, cela peut indiquer des 
                      informations importantes (blessures, changements de composition, etc.). Utilisez ces informations pour affiner 
                      votre analyse.
                    </p>
                  </div>
                </div>
              </div>
            </section>

            <section>
              <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">Erreurs courantes à éviter</h2>
              <p className="mb-4">
                Une erreur courante est de toujours choisir les cotes les plus élevées, pensant que cela maximise les gains. Cependant, 
                les cotes élevées correspondent généralement à des événements peu probables. Parier systématiquement sur les cotes élevées 
                sans analyse approfondie est une stratégie perdante à long terme.
              </p>
              <p className="mb-4">
                Ne confondez pas une cote élevée avec une bonne valeur. Une cote de 10.00 peut sembler attractive, mais si la probabilité 
                réelle de l'événement est de 5%, ce pari n'a pas de valeur. La valeur dépend de la différence entre la probabilité réelle 
                et la probabilité implicite de la cote, pas de la hauteur absolue de la cote.
              </p>
              <p>
                Évitez également de parier uniquement sur les favoris avec des cotes basses. Bien que ces paris gagnent plus fréquemment, 
                les gains sont généralement faibles et ne compensent pas toujours les pertes occasionnelles. Une approche équilibrée, 
                combinant différents types de paris selon la valeur identifiée, est généralement plus efficace.
              </p>
            </section>

            <section>
              <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">En résumé</h2>
              <p className="mb-4">
                Comprendre les cotes est essentiel pour réussir dans les paris sportifs. Les cotes décimales utilisées en France sont 
                simples à comprendre : multipliez votre mise par la cote pour obtenir votre gain total. Cependant, maîtriser l'art 
                d'interpréter les cotes et d'identifier la valeur nécessite de l'expérience et de l'analyse.
              </p>
              <p>
                Ne vous contentez pas de suivre les cotes. Faites votre propre analyse, comparez les cotes entre différents opérateurs, 
                et identifiez les opportunités où vous estimez que la probabilité réelle est supérieure à la probabilité implicite. 
                Cette approche, combinée à une gestion rigoureuse de votre bankroll, vous donne les meilleures chances de succès à long 
                terme dans les paris sportifs.
              </p>
            </section>
          </article>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default ComprendreCotesParis;

